import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { allPeopleData } from '../data/AllPeopleData';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  padding: 3rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  border-bottom: 2px solid #C4B5A0;
  padding-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    letter-spacing: 1.5px;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    letter-spacing: 1px;
    padding-bottom: 0.75rem;
  }
`;

const TopPeopleSection = styled.div`
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }
`;

const TopPeopleTitle = styled.h2`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    letter-spacing: 0.5px;
  }
`;

const TopPeopleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 600px;
  padding: 1rem;
  margin-bottom: 2rem;
  border: 1.5px solid #C4B5A0;
  background: #FFFFFF;
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.ink};
  border-radius: 2px;
  
  &::placeholder {
    color: #A89B88;
    font-style: italic;
  }
  
  &:focus {
    outline: none;
    border-color: #A89B88;
    box-shadow: 0 0 0 3px rgba(196, 181, 160, 0.2);
  }

  @media (max-width: 768px) {
    padding: 0.9rem;
    margin-bottom: 1.5rem;
    max-width: none;
  }

  @media (max-width: 480px) {
    padding: 0.8rem;
    margin-bottom: 1rem;
    font-size: 0.95rem;
  }
`;

const TopPersonCard = styled.div`
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  padding: 1.5rem;
  box-shadow: 
    1px 1px 5px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  
  &:hover {
    background: #FFF8F0;
    transform: translateY(-3px);
    box-shadow: 3px 3px 12px rgba(0, 0, 0, 0.15);
    border-color: #A89B88;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #D4847C 0%, #A89B88 100%);
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  color: white;
  border: 3px solid #C4B5A0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const AvatarInitial = styled.div`
  position: relative;
  z-index: 1;
`;

const Rank = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  background: #D4847C;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const TopPersonName = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.ink};
  font-weight: bold;
`;

const TopPersonMentions = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 1.3rem;
  color: #D4847C;
  font-weight: bold;
  margin-bottom: 0.25rem;
`;

const TopPersonLabel = styled.div`
  font-size: 0.8rem;
  color: #8A7E70;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 2px solid #C4B5A0;
  margin: 3rem 0 2rem 0;

  @media (max-width: 768px) {
    margin: 2rem 0 1.5rem 0;
  }

  @media (max-width: 480px) {
    margin: 1.5rem 0 1rem 0;
  }
`;

const ListSection = styled.div`
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  padding: 2rem;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 0;
  }
`;

const ListTitle = styled.h2`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    letter-spacing: 0.5px;
  }
`;

const PeopleList = styled.div`
  font-family: 'Courier New', monospace;
`;

const ListItem = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #E8DCC8;
  cursor: pointer;
  transition: background 0.15s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    background: #FFF8F0;
  }
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    padding: 0.65rem 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.6rem 0.5rem;
  }
`;

const ItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const ItemRank = styled.span`
  color: #A89B88;
  font-weight: bold;
  min-width: 60px;
  font-size: 0.9rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-width: 50px;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    min-width: 40px;
    font-size: 0.8rem;
  }
`;

const ItemName = styled.span`
  color: ${({ theme }) => theme.colors.ink};
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ItemCount = styled.span`
  color: #D4847C;
  font-weight: bold;
  min-width: 80px;
  text-align: right;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-width: 70px;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    min-width: 60px;
    font-size: 0.9rem;
  }
`;

const PaginationContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    gap: 0.4rem;
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
    gap: 0.3rem;
  }
`;

const PageButton = styled.button`
  background: ${props => props.$active ? '#D4847C' : '#F0E8D8'};
  color: ${props => props.$active ? 'white' : '#3A3A3A'};
  border: 1px solid #C4B5A0;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#C4736C' : '#E8DCC8'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.45rem 0.85rem;
    font-size: 0.85rem;
    min-width: 36px;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.7rem;
    font-size: 0.8rem;
    min-width: 32px;
  }
`;

const PageInfo = styled.span`
  font-family: 'Courier New', monospace;
  color: #716859;
  padding: 0 1rem;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    padding: 0 0.75rem;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.5rem;
    font-size: 0.8rem;
  }
`;

const ShowingCount = styled.p`
  margin-bottom: 1rem;
  font-family: 'Courier New', monospace;
  color: #716859;
  font-size: 0.95rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

// Map person names to their image filenames
const personImages = {
  'Jeffrey Epstein': '/images/people/jeff.webp',
  'Donald Trump': '/images/people/trump.jpeg',
  'Bill Clinton': '/images/people/clinton.avif',
  'Alan M Dershowitz': '/images/people/dershowitz.jpg',
  'Barack Obama': '/images/people/obama.webp',
};

function People() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [imageErrors, setImageErrors] = useState({});
  const ITEMS_PER_PAGE = 100;

  const handlePersonClick = (personName) => {
    // Navigate to search page with person name as query
    navigate(`/?q=${encodeURIComponent(personName)}`);
  };

  const handleImageError = (personName) => {
    setImageErrors(prev => ({ ...prev, [personName]: true }));
  };

  // Get top 5 people
  const topPeople = allPeopleData.slice(0, 5);

  // Filter people based on search (excluding top 5 from main list)
  const filteredPeople = allPeopleData.filter(person =>
    person.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredPeople.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPeople = filteredPeople.slice(startIndex, endIndex);
  
  // Reset to page 1 when search changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };
  
  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <>
      <SEO 
        title="People Mentioned" 
        description="List of people mentioned in the Jeffrey Epstein documents, sorted by frequency of appearance."
      />
      <PageContainer>
        <Title>People Mentioned in Documents</Title>
        
        {/* Top 5 People Section */}
        <TopPeopleSection>
          <TopPeopleTitle>Top 5 Most Mentioned</TopPeopleTitle>
          <TopPeopleGrid>
            {topPeople.map((person, index) => {
              const hasImage = personImages[person.name] && !imageErrors[person.name];
              return (
                <TopPersonCard
                  key={person.name}
                  onClick={() => handlePersonClick(person.name)}
                  title={`Click to search for ${person.name}`}
                >
                  <Rank>#{index + 1}</Rank>
                  <Avatar>
                    {hasImage ? (
                      <AvatarImage
                        src={personImages[person.name]}
                        alt={person.name}
                        onError={() => handleImageError(person.name)}
                      />
                    ) : (
                      <AvatarInitial>{person.name.charAt(0)}</AvatarInitial>
                    )}
                  </Avatar>
                  <TopPersonName>{person.name}</TopPersonName>
                  <TopPersonMentions>{person.count.toLocaleString()}</TopPersonMentions>
                  <TopPersonLabel>mentions</TopPersonLabel>
                </TopPersonCard>
              );
            })}
          </TopPeopleGrid>
        </TopPeopleSection>
        
        <Divider />
        
        {/* Complete List Section */}
        <ListSection>
          <ListTitle>All People ({allPeopleData.length.toLocaleString()} total)</ListTitle>
          
          <SearchInput
            type="text"
            placeholder="Search people by name..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          
          <ShowingCount>
            Showing {startIndex + 1}-{Math.min(endIndex, filteredPeople.length)} of {filteredPeople.length.toLocaleString()} people
            {searchQuery && ` matching "${searchQuery}"`}
          </ShowingCount>
          
          <PeopleList>
            {currentPeople.map((person, index) => {
              const absoluteRank = startIndex + index + 1;
              return (
                <ListItem
                  key={`${person.name}-${absoluteRank}`}
                  onClick={() => handlePersonClick(person.name)}
                  title={`Click to search for ${person.name}`}
                >
                  <ItemLeft>
                    <ItemRank>#{absoluteRank}</ItemRank>
                    <ItemName>{person.name}</ItemName>
                  </ItemLeft>
                  <ItemCount>{person.count.toLocaleString()}</ItemCount>
                </ListItem>
              );
            })}
          </PeopleList>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationContainer>
              <PageButton
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Prev
              </PageButton>
              
              {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                  <PageInfo key={`ellipsis-${idx}`}>...</PageInfo>
                ) : (
                  <PageButton
                    key={page}
                    $active={currentPage === page}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </PageButton>
                )
              ))}
              
              <PageButton
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </PageButton>
              
              <PageInfo>
                Page {currentPage} of {totalPages}
              </PageInfo>
            </PaginationContainer>
          )}
        </ListSection>
      </PageContainer>
    </>
  );
}

export default People;

